import type { GetServerSideProps, GetServerSidePropsContext } from 'next/types';
import React from 'react';
import dynamic from 'next/dynamic';
import { has, set } from 'lodash-es';
import ProductApi from '@lambda/apis/src/product';
import ProductGroupApi from '@lambda/apis/src/product-group';
import {
  ProductGroup,
  PopularProductGroup,
} from '@lambda/apis/src/product-group/types';
import { SearchResults } from '@/lib/search-helpers';
import { queryToSearch, runSearch } from '@/api/search-results';
import { VariantObj } from '@/lib/collections/catalogue/helpers';
import { getServerSideProps as getNewProductProps } from '@/components/collections/[handle]/NewProductPage/getServerSideProps';
import { serverSideFunction as getCollectionNavProps } from '@/components/collections-v2/collection-nav-page/getServerSideProps';
import {
  formatProductGroupAsLegacyCollectionNav,
  formatProductGroupAsLegacyCollectionSearch,
} from '@/lib/utils/helper';
import settings from '@/settings';
import { CollectionsNavPageProps } from '@/components/collections-v2/collection-nav-page/types';
import { NewProductProps } from '@/components/collections/[handle]/NewProductPage/types';

const SearchPage = dynamic(() => import('@/components/search/SearchPage'), {
  ssr: true,
});

const CollectionsNavPage = dynamic(
  () => import('@/components/collections-v2/collection-nav-page'),
  {
    ssr: true,
  },
);
const NewProductPageLayout = dynamic(
  () =>
    import(
      '@/components/collections/[handle]/NewProductPage/NewProductPageLayout'
    ),
  {
    ssr: true,
  },
);

type Path = { handle: string } & Partial<VariantObj>;

type PropsCollections = {
  handle?: string;
  popularProductGroups?: PopularProductGroup[];
} & SearchResults;

type Props =
  | ({ type: 'NEW_PRODUCT' } & NewProductProps)
  | ({ type: 'COLLECTIONS' } & PropsCollections)
  | ({ type: 'COLLECTIONS_NAV' } & CollectionsNavPageProps);

const REDIRECT_HOME = { redirect: { permanent: false, destination: '/' } };
const EMPTY_SEARCH: SearchResults = {
  offers: [],
  options: {},
  priceRange: { min: 0, max: 0 },
  hasNextPage: false,
  count: 0,
};

const getCollectionProps = async (
  event: GetServerSidePropsContext,
  productGroup: ProductGroup,
) => {
  const { params, query, res } = event;
  const handle = params?.handle as string;

  if (!handle) return REDIRECT_HOME;

  if (!productGroup) return REDIRECT_HOME;

  const legacyCollectionSearch =
    formatProductGroupAsLegacyCollectionSearch(productGroup);

  const searchQuery = await queryToSearch({
    query: legacyCollectionSearch.query,
    fullPhraseMatch: !!legacyCollectionSearch.query,
    brand: legacyCollectionSearch.filters.brand,
    categories: legacyCollectionSearch.filters.categories,
    psku: legacyCollectionSearch.filters.psku,
    sort: legacyCollectionSearch.sort,
    order: legacyCollectionSearch.order,
    ...query,
  });

  const categoryWarrantyInfo = null;

  const results = await runSearch(
    searchQuery,
    {
      brand: legacyCollectionSearch.filters.brand,
      categories: legacyCollectionSearch.filters.categories,
    },
    'COLLECTIONS',
  );

  if (results == null) return { props: { ...EMPTY_SEARCH, handle } };

  res.setHeader('Cache-Control', 'public, max-age=120');

  return { props: { handle, categoryWarrantyInfo, productGroup, ...results } };
};

export const getServerSideProps: GetServerSideProps<any, Path> = async (
  event,
) => {
  const rawHandle = event.query.handle;

  if (!rawHandle) return REDIRECT_HOME;

  const handle = decodeURIComponent(rawHandle as string);

  // I'm sorry, I have to hack this because the menu will bypass the redirection
  if (handle === 'other-smartphones') {
    return {
      redirect: {
        permanent: false,
        destination: '/collections/smartphones',
      },
    };
  }

  const productApi = new ProductApi();
  const productGroupApi = new ProductGroupApi();

  const product = await productApi.fetchProductByHandle(
    encodeURIComponent(handle as string),
  );

  if (product && productApi.isNewProduct(product)) {
    const response = await getNewProductProps(event);

    if (has(response, 'props')) set(response, 'props.type', 'NEW_PRODUCT');

    return response;
  }

  const productGroup = await productGroupApi.fetchGroupByHandle(
    encodeURIComponent(handle as string),
  );

  const categoryWarrantyInfo = null;

  if (productGroup && productGroupApi.isNavForGroupEnabled(productGroup)) {
    const nav = formatProductGroupAsLegacyCollectionNav(productGroup);
    const result = await getCollectionNavProps(
      event.query,
      nav.fixedFilters,
      productGroup,
    );

    const hideFilters = [];
    const categoryOptions = result.result.options?.categories ?? [];

    // Handle Filters
    const brandOptions = result.result.options?.brand ?? [];

    if (categoryOptions.length < 2) hideFilters.push('categories');
    if (brandOptions.length < 2) hideFilters.push('brand');

    const trackingPageType = [];

    if (productGroup.filters?.categories) trackingPageType.push('Category');
    if (productGroup.filters?.brand) trackingPageType.push('Brand');

    return {
      props: {
        ...result,
        hideFilters,
        title: productGroup.name,
        trackingPageType:
          productGroup?.trackingPageType ?? trackingPageType.join(' + '),
        handle: event.query.handle,
        productGroup,
        categoryWarrantyInfo,
        type: 'COLLECTIONS_NAV',
      },
    };
  }

  if (!productGroup) return REDIRECT_HOME;

  const response = await getCollectionProps(event, productGroup);

  if (has(response, 'props')) {
    set(response, 'props.type', 'COLLECTIONS');
    set(response, 'props.productGroups', productGroup);
    set(
      response,
      'props.popularProductGroups',
      await productGroupApi.fetchPopularProductGroups(settings.store),
    );
    set(response, 'props.categoryWarrantyInfo', categoryWarrantyInfo);
  }

  return response;
};

export default function CollectionPages(props: Props) {
  if (props.type === 'COLLECTIONS') {
    return (
      <SearchPage
        key={props.handle}
        {...props}
        trackingPageTitle="Collection"
      />
    );
  }

  if (props.type === 'NEW_PRODUCT')
    return <NewProductPageLayout {...props} key={props.handle} />;

  if (props.type === 'COLLECTIONS_NAV')
    return <CollectionsNavPage {...props} key={props.handle} />;

  return null;
}
